// src/pages/TourResultsPage.jsx
// Tour results page: 70% itinerary | 30% chat panel
// Accessed via /results?plan={planId}

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  MapPin, Calendar, Users, Wine, Send, RotateCcw,
  ChevronRight, ChevronDown, Clock, Bookmark, ExternalLink, Map,
  Image as ImageIcon, CalendarCheck, Hotel
} from "lucide-react";
import TourMap from "../components/TourMap";
import BookingModal from "../components/BookingModal";
import BookTourStepper from "../components/BookTourStepper";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Same tomorrow-8AM default PlanPage.jsx uses — a fallback only, for when
// this results page is opened without having gone through the Plan page's
// departure picker in this browser session (e.g. a direct/shared link).
function defaultDepartureAt() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T08:00`;
}

// Deterministic trip_ref, derived from (plan_id, departure_at) rather than
// randomly generated per checkout — so a booking made via the ad-hoc "Book
// tasting" flow and one made via the "Book this tour" stepper, for the same
// plan and departure time, always land in the same trip and count toward
// each other's earliest-arrival/dwell-time math (see mcp-booking's
// get_plan_stop_slots). Re-deriving it from the same inputs on every visit
// also means it survives a page refresh with no state to persist. Not a
// security boundary — just a stable grouping key — so an ordinary SHA-256
// digest reshaped into UUID form is enough; no need for RFC 4122 v5 exactness.
async function deriveTripRef(planId, departureAt) {
  const data = new TextEncoder().encode(`${planId}|${departureAt}`);
  const hashBuf = await crypto.subtle.digest("SHA-256", data);
  const hex = [...new Uint8Array(hashBuf).slice(0, 16)].map(b => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROAD_NAMES = {
  "ar-valley":     "Ahr Valley",
  "vienna-venice": "Vienna – Venice",
  "malopolska":    "Małopolska",
};

const _fmtField = (v) => (v && v !== "undefined") ? v : "…";

function WineField({ cat, v }) {
  const shown = _fmtField(v);
  return (
    <div style={{ display: "flex", gap: 6, fontSize: 10.5 }}>
      <span style={{ color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9, width: 72, flexShrink: 0 }}>{cat}</span>
      <span style={{ color: shown === "…" ? "#A8B89A" : "#2C3820", fontWeight: 500, textTransform: "capitalize", textAlign: "left" }}>{shown}</span>
    </div>
  );
}

// ── Wine passport card (results-page winery detail modal) ──────────────────
function WineCard({ w }) {
  const [expanded, setExpanded] = useState(false);
  const mainFields = [
    ["Colour", w.colour_name?.en],
    ["Sugar", w.sugar_type_name?.en],
    ["CO2", w.co2_type_name?.en],
    ["Price range", w.price_range_name?.en],
  ];
  const moreFields = [
    ["Spirit", w.spirit_type_name?.en],
    ["Special", w.special_type_name?.en],
    ["Assemblage", w.assemblage_type_name?.en],
    ["Origin", w.origin_control_level_name?.en],
    ["Grape", w.grape_variety_name],
  ];

  return (
    <div style={{ position: "relative", background: "#E8EDE0", border: "1px solid #252525", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: w.hex_hint || "#6B2737" }} />

      {/* Top row: image + main fields — height is fixed by this row's own content,
          independent of the expand toggle below, so the image never rescales. */}
      <div style={{ display: "flex", padding: "12px 14px 0 10px" }}>
        <div style={{
          width: 100, alignSelf: "stretch", margin: "0 14px 0 6px", borderRadius: 8,
          overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
          border: w.image_url ? "1px solid #252525" : "1px dashed #C4D0A8",
          background: w.image_url ? `#EDF1E4 url(${w.image_url}) center/cover no-repeat` : "#EDF1E4",
          color: "#A8B89A",
        }}>
          {!w.image_url && <ImageIcon size={30} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, color: "#1E2C14", fontWeight: 600 }}>{w.name}</div>
          {w.vintage_year && <div style={{ fontSize: 10, color: "#7A8E68", marginTop: 2, letterSpacing: "0.03em" }}>Vintage {w.vintage_year}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 10 }}>
            {mainFields.map(([cat, v]) => <WineField key={cat} cat={cat} v={v} />)}
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "8px 14px 0", paddingTop: 8, borderTop: "1px dashed #C4D0A8" }}>
          {moreFields.map(([cat, v]) => <WineField key={cat} cat={cat} v={v} />)}
        </div>
      )}

      <div style={{ padding: "8px 0 10px", display: "flex", justifyContent: "center" }}>
        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "none", color: "#886070", fontSize: 10.5,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 3, padding: "2px 8px",
        }}>
          {expanded ? "Show less" : "Show more"}
          <ChevronDown size={11} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
        </button>
      </div>
    </div>
  );
}

// ── Tour itinerary panel (70%) ────────────────────────────────────────────────

// ETA formatting: same value both booking flows use to decide feasibility
// (mcp-booking's earliest_arrival), shown here purely as information —
// "you'd arrive around this local time if you keep to the plan." Shows the
// date too when it differs from the leg's own day number would suggest
// (e.g. a stop that spills past midnight), so it's never ambiguous.
function fmtEta(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}
function fmtBookedWhen(visitDate, slotTime) {
  try {
    const d = new Date(`${visitDate}T00:00:00`);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${(slotTime || "").slice(0, 5)}`;
  } catch { return `${visitDate}, ${slotTime}`; }
}

// leg.arrival_minutes (solver-a's min_time_tw schedule — elapsed minutes
// since departure_at, see model.mod's a[k,i]/cond_tw_* and solver-a/
// handler.py's get_tw_schedule) is only ever present on a time-window-
// respecting solve. Converting it back to a wall-clock time here surfaces
// the *planned*, opening-hours-respecting schedule the solver actually
// committed to — distinct from WineryStopCard's separate live "ETA" below,
// which re-derives feasibility on demand from mcp-booking. Both should
// usually agree; showing the planned one costs no extra API call and
// explains *why* the optimizer ordered the day the way it did (2026-07-27
// audit — this data existed on every leg already but was never rendered).
function fmtPlannedArrival(departureAt, arrivalMinutes) {
  if (!departureAt || arrivalMinutes == null) return null;
  try {
    const d = new Date(departureAt);
    d.setMinutes(d.getMinutes() + arrivalMinutes);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}

// One winery stop, merged from what used to be two separate views: the
// day-list's rich card (description, highlights, verified badge, Book/
// Details actions) and the leg-by-leg breakdown's dwelling stats (time,
// cost, photo). Adds a live ETA (mcp-booking's earliest_arrival — the same
// number that governs actual booking feasibility, not a separate rough
// estimate) and, when this stop already has a pending/approved booking for
// this trip, shows that instead of a "Book tasting" button.
function WineryStopCard({ leg, legIndex, readOnly = false, status, onSelectWinery, onBookWinery, userEmail, departureAt }) {
  const w = leg.winery || {};
  const eta = fmtEta(status?.eta);
  const plannedArrival = fmtPlannedArrival(departureAt, leg.arrival_minutes);
  return (
    <div style={{ background: "#EDF1E4", border: "1px solid #222", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 8, flexShrink: 0, overflow: "hidden",
          background: leg.title_image_url ? `#E8EDE0 url(${leg.title_image_url}) center/cover no-repeat` : "#E8EDE0",
          border: "1px solid #252525", display: "flex", alignItems: "center", justifyContent: "center", color: "#A8B89A",
        }}>
          {!leg.title_image_url && <ImageIcon size={22} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
            <Wine size={12} color="#886070" />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#2C3820" }}>{leg.to}</span>
            {w.verification_status === "verified" && (
              <span title="Verified by road representative" style={{fontSize:10,padding:"1px 6px",borderRadius:100,background:"rgba(61,74,26,0.12)",color:"#3D4A1A",fontWeight:600}}>
                ✓ Verified
              </span>
            )}
          </div>
          {w.description && (
            <p style={{ fontSize: 12, color: "#5A6E48", margin: "0 0 6px", lineHeight: 1.4 }}>{w.description}</p>
          )}
          {w.highlights?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
              {w.highlights.map((h, j) => (
                <span key={j} style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 100,
                  background: "rgba(107,39,55,0.08)", color: "#886070",
                  border: "1px solid rgba(107,39,55,0.15)",
                }}>{h}</span>
              ))}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 5, columnGap: 14, fontSize: 11.5 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#7A8E68" }}>Dwelling time</span>
              <span style={{ color: "#2C3820" }}>{Math.round(leg.visit_minutes)} min</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#7A8E68" }}>Dwelling cost</span>
              <span style={{ color: "#2C3820" }}>{leg.budget_estimate?.tastings}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ETA — live, from the same feasibility calc booking uses */}
      {!readOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8, fontSize: 11, color: "#6A7E58" }}>
          <Clock size={10} />
          {status?.loading ? "Calculating ETA…" : eta ? <>ETA ~{eta}</> : status?.feasible === false && !status?.eta ? "Outside trip window" : null}
        </div>
      )}

      {/* Planned arrival — the opening-hours-respecting schedule the
          optimizer actually solved for (solver-a's min_time_tw), shown
          whenever the live ETA above isn't available yet/at all */}
      {!eta && plannedArrival && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, fontSize: 11, color: "#7A8E68" }}>
          <Clock size={10} />
          Planned arrival ~{plannedArrival}
        </div>
      )}

      {/* Booking status / actions */}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        {readOnly ? null : status?.existingBooking ? (
          <div style={{flex:1,padding:"7px 10px",borderRadius:7,background:"rgba(74,112,82,0.12)",border:"1px solid rgba(74,112,82,0.3)",fontSize:11,color:"#3D6B45",textAlign:"center",fontWeight:500}}>
            ✓ Booked {fmtBookedWhen(status.existingBooking.visit_date, status.existingBooking.slot_time)} — {status.existingBooking.status}
          </div>
        ) : userEmail ? (
          <button onClick={() => onBookWinery && onBookWinery(legIndex)} style={{
            flex: 1, padding: "7px 10px", borderRadius: 7,
            background: "#6B2737", border: "none",
            color: "#DCE4EC", fontSize: 11, fontWeight: 500,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          }}>
            <Bookmark size={11} /> Book tasting
          </button>
        ) : (
          <div style={{flex:1,padding:"7px 10px",borderRadius:7,background:"#EDF1E4",border:"1px solid #C4D0A8",fontSize:12,color:"#6A7E58",textAlign:"center"}}>
            Add email to enable booking
          </div>
        )}
        <button onClick={() => onSelectWinery && onSelectWinery(legIndex)} style={{
          flex: 1, padding: "7px 10px", borderRadius: 7,
          background: "#E8EDE0", border: "1px solid #2A2A2A",
          color: "#5A6E48", fontSize: 11,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
        }}>
          <ExternalLink size={11} /> Details
        </button>
      </div>
    </div>
  );
}

// Overnight-stop card — the day-boundary city (Maribor, Gorizia, ... on
// vienna-venice) the traveler actually sleeps in between two driving days.
// Rendered as BOTH the last item of the day that ends there and the first
// item of the day that begins there (same city, same content, shown
// twice on purpose — see the itinerary builder below) so the results page
// visually confirms "yes, this is the same overnight stop closing one day
// and opening the next," matching the solver's own continuous-clock
// schedule. Content (2-3 lodging suggestions + 2-3 points of interest)
// comes from summarization/handler.py's web-search-grounded
// fetch_overnight_stop_web, keyed by city name on tour.overnight_stops —
// unverified suggestions, not a live booking source (there's no
// "Book tasting"-style action here, unlike WineryStopCard).
function OvernightStopCard({ city, stop, overnight = true }) {
  const accommodations = stop?.accommodations || [];
  const pois = stop?.points_of_interest || [];
  return (
    <div style={{ background: "#EDF1E4", border: "1px solid #222", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <MapPin size={13} color="#886070" />
        <span style={{ fontSize: 13, fontWeight: 600, color: "#2C3820" }}>{overnight ? `Overnight in ${city}` : city}</span>
      </div>

      {accommodations.length > 0 && (
        <div style={{ marginBottom: pois.length > 0 ? 10 : 0 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
            <Hotel size={11} /> Where to stay
          </div>
          {accommodations.map((a, i) => (
            <div key={i} style={{ fontSize: 12, color: "#2C3820", marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>{a.name}</span>
              {a.price_range && <span style={{ color: "#886070" }}> · {a.price_range}</span>}
              {a.description && <div style={{ fontSize: 11.5, color: "#5A6E48" }}>{a.description}</div>}
            </div>
          ))}
        </div>
      )}

      {pois.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
            <MapPin size={11} /> Worth a look
          </div>
          {pois.map((p, i) => (
            <div key={i} style={{ fontSize: 12, color: "#2C3820", marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>{p.name}</span>
              {p.description && <div style={{ fontSize: 11.5, color: "#5A6E48" }}>{p.description}</div>}
            </div>
          ))}
        </div>
      )}

      {accommodations.length === 0 && pois.length === 0 && (
        <p style={{ fontSize: 11.5, color: "#886070", margin: 0 }}>
          No lodging suggestions available yet for {city} — search for accommodation options before booking.
        </p>
      )}

      <p style={{ fontSize: 10, color: "#886070", marginTop: 8, marginBottom: 0 }}>
        Suggestions only — verify availability and current details before booking.
      </p>
    </div>
  );
}

// Lightweight continuation line for a day that STARTS at yesterday's
// overnight city — the full lodging/POI recommendations were already
// shown once, as the previous day's closing OvernightStopCard, so
// repeating that whole card here would just be the same content twice.
// This is deliberately just a one-line "you're leaving from here, at
// about this time" cue, not a second full card. The departure time is
// derived from the day's own first leg: get_tw_schedule/get_legs only
// stamp arrival_minutes at a leg's DESTINATION, so departure-from-the-
// overnight-city = that arrival time minus the leg's own travel time.
function DepartureNote({ city, minutes, departureAt }) {
  const time = minutes != null ? fmtPlannedArrival(departureAt, minutes) : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6A7E58", padding: "2px 2px 12px" }}>
      <Clock size={11} />
      Departing from {city}{time ? ` at ${time}` : ""}
    </div>
  );
}

// One day's worth of legs — travel hop, winery stop, travel hop, winery
// stop, ... in the exact sequence they happen. Replaces the old separate
// day-list/leg-by-leg split: every leg the day contains is shown once,
// in order, at full detail. overnightBefore/overnightAfter (each
// {city, stop} or null) bookend the day's own entries when this day
// starts/ends at a real overnight stop rather than the trip's true
// start/end depot (e.g. Vienna/Venice never get one) — overnightBefore
// renders as the short DepartureNote (see above), overnightAfter as the
// full OvernightStopCard, since that's the first time this day's reader
// sees that city's recommendations.
function DayBlock({ dayNumber, entries, readOnly = false, overnightBefore, overnightAfter, overnightStops, onSelectWinery, onBookWinery, userEmail, legStatus, departureAt }) {
  const [expanded, setExpanded] = useState(true);
  const totalKm = entries.reduce((sum, { leg }) => sum + (leg.driving_km || 0), 0);
  const title = entries.filter(e => e.leg.is_winery_stop).map(e => e.leg.to).join(" & ") || `Day ${dayNumber}`;
  return (
    <div style={{ background: "#EDF1E4", border: "1px solid #252525", borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
      <button onClick={() => setExpanded(e => !e)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 16px", background: "none", border: "none", cursor: "pointer",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(107,39,55,0.15)", border: "1px solid rgba(107,39,55,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#886070", flexShrink: 0,
          }}>{dayNumber}</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1E2C14" }}>Day {dayNumber} — {title}</div>
            {totalKm > 0 && <div style={{ fontSize: 11, color: "#6A7E58", marginTop: 2 }}>~{Math.round(totalKm)} km driving</div>}
          </div>
        </div>
        <ChevronRight size={16} color="#6A7E58" style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
      </button>

      {expanded && (
        <div style={{ padding: "0 16px 16px" }}>
          {overnightBefore && (() => {
            const firstLeg = entries[0]?.leg;
            const departMinutes = (firstLeg?.arrival_minutes != null && firstLeg?.travel_minutes != null)
              ? firstLeg.arrival_minutes - firstLeg.travel_minutes
              : null;
            return <DepartureNote city={overnightBefore.city} minutes={departMinutes} departureAt={departureAt} />;
          })()}
          {entries.map(({ leg, legIndex }, i) => {
            // A depot this leg arrives at, mid-day (not the day's own
            // last entry — that's the real overnight stop, already
            // rendered below as the full "Overnight in X" card outside
            // this loop). The traveler drives straight through it either
            // way, so it still gets the same lodging/POI content, just
            // framed as a plain waypoint rather than an overnight stop
            // (see summarization/handler.py::find_waypoint_cities).
            const isLastEntry = i === entries.length - 1;
            const waypointStop = !leg.is_winery_stop && !isLastEntry ? overnightStops?.[leg.to] : null;
            return (
              <React.Fragment key={legIndex}>
                <TravelCard leg={leg} />
                {leg.is_winery_stop && (
                  <WineryStopCard
                    leg={leg} legIndex={legIndex} readOnly={readOnly} status={legStatus?.[legIndex]}
                    onSelectWinery={onSelectWinery} onBookWinery={onBookWinery} userEmail={userEmail}
                    departureAt={departureAt}
                  />
                )}
                {waypointStop && <OvernightStopCard city={leg.to} stop={waypointStop} overnight={false} />}
              </React.Fragment>
            );
          })}
          {overnightAfter && <OvernightStopCard city={overnightAfter.city} stop={overnightAfter} />}
        </div>
      )}
    </div>
  );
}

// Lazily loads Leaflet once (shared across every LegMiniMap instance —
// "Show more" mounts one per leg simultaneously, so this must not inject
// the script/css tags more than once). Mirrors components/TourMap.jsx's
// loading approach, just as a reusable promise instead of a one-off effect.
let _leafletLoadPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (_leafletLoadPromise) return _leafletLoadPromise;
  _leafletLoadPromise = new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error("Map unavailable"));
    document.head.appendChild(script);
  });
  return _leafletLoadPromise;
}

// Same free OSRM public router TourMap.jsx uses for the full-tour route —
// returns an array of [lat,lng] points along the actual road, or null on
// any failure (caller falls back to a straight line between the two points).
async function fetchOsrmRoute(from, to) {
  const coords = `${from[1]},${from[0]};${to[1]},${to[0]}`;
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data.code === "Ok" && data.routes[0]) {
      return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    }
  } catch (e) {
    console.warn("OSRM routing failed:", e);
  }
  return null;
}

// Small, non-interactive start/end preview for one travel leg — not the
// full route (see TourMap.jsx for that). Coordinates come from
// summarization/handler.py::build_leg_breakdown (from_lat/from_lng/to_lat/
// to_lng), the same depot/winery coordinates used to compute travel_minutes,
// so the preview always agrees with the stated drive time. Draws the actual
// road route via OSRM (same as TourMap.jsx), falling back to a straight
// dashed line if OSRM is unreachable.
function LegMiniMap({ fromLat, fromLng, toLat, toLng }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [error, setError] = useState("");
  const hasCoords = fromLat != null && fromLng != null && toLat != null && toLng != null;

  useEffect(() => {
    if (!hasCoords) return;
    let cancelled = false;
    loadLeaflet().then(async (L) => {
      if (cancelled || !mapRef.current || mapInstanceRef.current) return;
      const map = L.map(mapRef.current, {
        zoomControl: false, attributionControl: false, dragging: false,
        scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false,
        keyboard: false, touchZoom: false, tap: false,
      });
      mapInstanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
      const from = [fromLat, fromLng], to = [toLat, toLng];
      L.circleMarker(from, { radius: 5, color: "#6A7E58", fillColor: "#6A7E58", fillOpacity: 1, weight: 1 }).addTo(map);
      L.circleMarker(to, { radius: 5, color: "#6B2737", fillColor: "#6B2737", fillOpacity: 1, weight: 1 }).addTo(map);
      map.fitBounds(L.latLngBounds([from, to]), { padding: [16, 16] });

      const roadLine = await fetchOsrmRoute(from, to);
      if (cancelled || !mapInstanceRef.current) return;
      if (roadLine) {
        L.polyline(roadLine, { color: "#6B2737", weight: 3, opacity: 0.8 }).addTo(map);
        map.fitBounds(L.latLngBounds(roadLine), { padding: [16, 16] });
      } else {
        L.polyline([from, to], { color: "#6B2737", weight: 2, opacity: 0.6, dashArray: "4 4" }).addTo(map);
      }
    }).catch(() => setError("Map unavailable"));

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, [hasCoords, fromLat, fromLng, toLat, toLng]);

  if (!hasCoords) return null;

  return (
    <div style={{ width: "100%", height: 90, borderRadius: 6, overflow: "hidden", border: "1px solid #222", marginTop: 8, position: "relative" }}>
      {error ? (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#886070", background: "#E8EDE0" }}>{error}</div>
      ) : (
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      )}
    </div>
  );
}

// The hop into a point — Vienna -> Winery1, Winery1 -> Maribor, etc.
// Rendered for every leg, winery-stop or not.
function TravelCard({ leg }) {
  return (
    <div style={{ background: "#EDF1E4", border: "1px solid #222", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#2C3820" }}>
          {leg.from} → {leg.to}
        </span>
        <span style={{ fontSize: 11, color: "#6A7E58", display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
          <Clock size={10} />{Math.round(leg.travel_minutes)} min
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 5, columnGap: 14, fontSize: 11.5 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#7A8E68" }}>Driving time</span>
          <span style={{ color: "#2C3820" }}>~{leg.driving_km} km · {Math.round(leg.travel_minutes)} min</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#7A8E68" }}>Driving cost</span>
          <span style={{ color: "#2C3820" }}>{leg.budget_estimate?.travel}</span>
        </div>
      </div>
      <LegMiniMap fromLat={leg.from_lat} fromLng={leg.from_lng} toLat={leg.to_lat} toLng={leg.to_lng} />
    </div>
  );
}

// Surfaces solver-a's max_wine_coverage result (model.mod's nW/WMatch/z/
// cond13/wcov — see orchestrator's _wine_epsilon_sweep): how much of the
// PlanPage wine-filter selection this tour's wineries collectively cover,
// and whether that meets the requested satisfaction level within the
// chosen time-deviation budget.
function WineCoverageCard({ coverage }) {
  const {
    target_wine_count, covered_wine_count, achieved_pct,
    requested_min_match_pct, time_deviation_minutes, feasible,
  } = coverage;
  const devHours = time_deviation_minutes != null ? Math.round(time_deviation_minutes / 6) / 10 : null;
  return (
    <div style={{
      background: feasible ? "rgba(107,39,55,0.06)" : "rgba(180,90,30,0.08)",
      border: `1px solid ${feasible ? "rgba(107,39,55,0.2)" : "rgba(180,90,30,0.3)"}`,
      borderRadius: 10, padding: "14px 16px", marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Wine size={13} color="#886070" />
        <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>
          Wine filter coverage
        </p>
      </div>
      <p style={{ fontSize: 13, color: "#2C3820", margin: "0 0 4px" }}>
        This tour covers <strong>{covered_wine_count} of {target_wine_count}</strong> selected wines
        ({achieved_pct}% of your filter){devHours != null ? ` — using +${devHours}h of your time-deviation budget` : ""}.
      </p>
      {feasible ? (
        <p style={{ fontSize: 11.5, color: "#5A6E48", margin: 0 }}>
          Meets your requested satisfaction level ({requested_min_match_pct}%).
        </p>
      ) : (
        <p style={{ fontSize: 11.5, color: "#8A5A20", margin: 0 }}>
          Couldn't fully reach your requested satisfaction level ({requested_min_match_pct}%) within your
          chosen time-deviation budget. Try raising the "Time deviation" slider on the plan page for a
          better match.
        </p>
      )}
    </div>
  );
}

// solver-a's Phase 3 sweep solves max_wine_coverage once per time-deviation
// point in a SINGLE Lambda invocation (orchestrator's _wine_epsilon_sweep),
// so the whole time-vs-coverage trade-off curve comes back with the primary
// plan — no extra solver calls needed to show it (this replaces Phase B's
// on-demand /alternate-route endpoint). Read-only: only the primary
// (largest-deviation) point gets a full, Haiku-enriched itinerary — building
// that for every curve point would mean re-running enrichment per point,
// which defeats the point of returning the curve for free.
function WineCoverageCurve({ curve }) {
  if (!curve || curve.length < 2) return null;
  return (
    <div style={{
      background: "#EDF1E4", border: "1px solid #222", borderRadius: 10,
      padding: "14px 16px", marginBottom: 20,
    }}>
      <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
        Time vs. wine-filter coverage
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {curve.map((pt, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
            <span style={{ color: "#6A7E58" }}>
              +{Math.round((pt.epsilon_minutes || 0) / 6) / 10}h
              {i === curve.length - 1 && <span style={{ color: "#886070", fontWeight: 600 }}> (this route)</span>}
            </span>
            <span style={{ color: "#2C3820" }}>
              {pt.covered_wine_count} wines ({pt.achieved_pct}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Picks out which entry in tour.solver_solutions IS the currently-shown
// route, then everything solver-a computed as a tied alternate to it
// (min_time_tw_alt / max_rating_tw_alt / max_feedback_tw_alt /
// max_wine_coverage_tw_alt — see solver-a/handler.py's Phase 2/3 and
// model.mod's cond10/11/12/14, 2026-07-27). Read-only, same reasoning as
// WineCoverageCurve above: only the currently-shown route got a full,
// Haiku-enriched day-by-day itinerary — re-running that per alternate
// would mean N extra enrichment passes just to let someone compare
// options, so alternates are shown as a lightweight route + numbers
// preview instead.
function findAlternates(tour) {
  const sols = tour?.solver_solutions || [];
  const optimal = tour?.optimal_route || [];
  const primary = sols.find(s =>
    !String(s.type || "").endsWith("_alt") &&
    Array.isArray(s.route_names) &&
    s.route_names.length === optimal.length &&
    s.route_names.every((n, i) => n === optimal[i])
  );
  if (!primary) return { primary: null, alternates: [] };
  const altType = `${primary.type}_alt`;
  const seen = new Set();
  const alternates = sols.filter(s => {
    if (s.type !== altType) return false;
    if (primary.epsilon !== undefined && s.epsilon !== primary.epsilon) return false;
    const key = (s.route_names || []).join("|");
    if (seen.has(key) || key === (primary.route_names || []).join("|")) return false;
    seen.add(key);
    return true;
  });
  return { primary, alternates };
}

// The single number worth showing per alternate, whichever objective this
// tour was optimized for — mirrors how each solution type carries its own
// value field (see solver-a/handler.py's Phase 1/3).
function altHeadline(sol) {
  if (sol.rating != null) return `★ ${sol.rating.toFixed ? sol.rating.toFixed(1) : sol.rating} rating`;
  if (sol.feedback != null) return `${Math.round(sol.feedback)} reviews`;
  if (sol.wine_coverage_pct != null) return `${sol.wine_coverage_pct}% of selected wines`;
  return null;
}

// Groups a tour's flat leg list into per-day DayBlocks (travel hop, winery
// stop, ... in order) and detects the overnight city bookending adjacent
// days. Used for the main itinerary and, readOnly, for alternate routes.
function DayList({ legs, overnightStops, readOnly = false, onSelectWinery, onBookWinery, userEmail, legStatus, departureAt }) {
  const byDay = {};
  (legs || []).forEach((leg, legIndex) => {
    const d = leg.day || 1;
    (byDay[d] ||= []).push({ leg, legIndex });
  });
  const dayNumbers = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  // Detect overnight boundary cities: the destination of day N's last leg
  // is the SAME city as the origin of day N+1's first leg — guaranteed by
  // solver-a's single continuous elapsed-time clock across segments
  // (cond_tw_chain) whenever a day boundary falls mid-route. Mirrors
  // summarization/handler.py::find_overnight_cities server-side.
  const overnightAfterDay = {};
  for (let i = 0; i < dayNumbers.length - 1; i++) {
    const d = dayNumbers[i], dNext = dayNumbers[i + 1];
    const lastLeg = byDay[d][byDay[d].length - 1]?.leg;
    const firstLegNext = byDay[dNext][0]?.leg;
    if (lastLeg?.to && firstLegNext?.from && lastLeg.to === firstLegNext.from) {
      overnightAfterDay[d] = lastLeg.to;
    }
  }

  return dayNumbers.map((d, i) => {
    const cityBefore = i > 0 ? overnightAfterDay[dayNumbers[i - 1]] : null;
    const cityAfter = overnightAfterDay[d] || null;
    return (
      <DayBlock
        key={d} dayNumber={d} entries={byDay[d]} readOnly={readOnly}
        overnightBefore={cityBefore ? overnightStops?.[cityBefore] : null}
        overnightAfter={cityAfter ? overnightStops?.[cityAfter] : null}
        overnightStops={overnightStops}
        onSelectWinery={onSelectWinery} onBookWinery={onBookWinery}
        userEmail={userEmail} legStatus={legStatus} departureAt={departureAt}
      />
    );
  });
}

function AlternateRouteRow({ sol, tour, onSwitch, switching }) {
  const [expanded, setExpanded] = useState(false);
  // Switching needs the alternate's full enriched legs
  // (summarization/handler.py's alternate_itineraries, keyed by the route
  // joined with "|"); absent on tours generated before that field existed.
  const canSwitch = !!tour?.alternate_itineraries?.[(sol.route_names || []).join("|")];
  return (
    <div style={{ border: "1px solid #222", borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
      <button onClick={() => setExpanded(e => !e)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "10px 12px", background: "#EDF1E4", border: "none", cursor: "pointer", textAlign: "left",
      }}>
        <div>
          <div style={{ fontSize: 12.5, color: "#2C3820", fontWeight: 500 }}>
            {(sol.route_names || []).join(" → ")}
          </div>
          <div style={{ fontSize: 11, color: "#6A7E58", marginTop: 2, display: "flex", gap: 10 }}>
            <span>{Math.floor((sol.time || 0) / 60)}h {Math.round(sol.time || 0) % 60}min</span>
            {altHeadline(sol) && <span>{altHeadline(sol)}</span>}
          </div>
        </div>
        <ChevronRight size={14} color="#6A7E58" style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
      </button>
      {expanded && (
        <div style={{ padding: "8px 12px", background: "#F2F5EC" }}>
          {(sol.legs || []).map((leg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, padding: "4px 0", borderBottom: i < sol.legs.length - 1 ? "1px solid #DCE4D0" : "none" }}>
              <span style={{ color: "#2C3820" }}>{leg.from} → {leg.to}</span>
              <span style={{ color: "#6A7E58" }}>
                {Math.round(leg.travel_minutes)} min drive
                {leg.visit_minutes > 0 && ` · ${Math.round(leg.visit_minutes)} min visit`}
              </span>
            </div>
          ))}
          {canSwitch ? (
            <button onClick={() => onSwitch(sol)} disabled={switching} style={{
              marginTop: 10, width: "100%", padding: "8px 10px", borderRadius: 7,
              background: "#6B2737", border: "none", color: "#DCE4EC", fontSize: 12, fontWeight: 500,
              cursor: switching ? "default" : "pointer", opacity: switching ? 0.6 : 1,
            }}>
              {switching ? "Switching…" : "Switch to alternative itinerary"}
            </button>
          ) : (
            <p style={{ fontSize: 10.5, color: "#886070", marginTop: 8, marginBottom: 0 }}>
              Preview only — this tour was generated before route switching was available.
              Re-generate it from the Plan page to enable switching.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AlternateRoutesCard({ tour, onSwitch, switching }) {
  const { alternates } = findAlternates(tour);
  if (!alternates.length) return null;
  return (
    <div style={{
      background: "#EDF1E4", border: "1px solid #222", borderRadius: 10,
      padding: "14px 16px", marginBottom: 20,
    }}>
      <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
        {alternates.length === 1 ? "1 alternate route" : `${alternates.length} alternate routes`} — tied for the same result
      </p>
      {alternates.map((sol, i) => <AlternateRouteRow key={(sol.route_names || []).join("|")} sol={sol} tour={tour} onSwitch={onSwitch} switching={switching} />)}
    </div>
  );
}

function ItineraryPanel({ tour, narrative, onShowMap, onSelectWinery, onBookWinery, onSwitchAlternative, switchingAlternative, userEmail, canBookTour, onBookTour, legStatus, departureAt }) {
  if (!tour) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",color:"#6A7E58",gap:12}}>
      <div style={{fontSize:32}}>🍷</div>
      <div style={{fontSize:14,color:"#5A6E48"}}>Your tour is being prepared...</div>
      <div style={{fontSize:11,color:"#7A8E68"}}>This usually takes under a minute</div>
    </div>
  );
  return (
    // Right padding is wider than the other sides on purpose: the chat
    // toggle tab (TourResultsPage) is `position: fixed; right: 0` at
    // top:30%, so it permanently overlaps a ~56px-wide vertical strip at
    // the right edge of the viewport regardless of scroll position. Full-
    // width card content (most visibly the leg mini-maps, but really any
    // card) would otherwise render underneath it. This keeps every card
    // clear of that strip instead of just special-casing the map.
    <div style={{ height: "100%", overflowY: "auto", padding: "24px 56px 24px 20px" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 4 }}>
          Your tour
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, color: "#1E2C14", margin: 0 }}>
            {ROAD_NAMES[tour.road_slug] || tour.road_slug}
          </h1>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {canBookTour && (
              <button onClick={onBookTour} style={{
                display: "flex", alignItems: "center", gap: 5,
                background: "#6B2737", border: "none", borderRadius: 7,
                color: "#DCE4EC", cursor: "pointer", fontSize: 11, padding: "6px 12px", fontWeight: 500,
              }}><CalendarCheck size={12} /> Book this tour</button>
            )}
            {onShowMap && <button onClick={onShowMap} style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "#E2D8DA", border: "1px solid #2E1E22", borderRadius: 7,
              color: "#886070", cursor: "pointer", fontSize: 11, padding: "6px 12px",
            }}><Map size={12} /> Map</button>}
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { icon: <Calendar size={12} />, label: `${tour.total_days || tour.days} days` },
            { icon: <Users size={12} />, label: `${tour.party_size} people` },
            { icon: <Wine size={12} />, label: `${tour.total_wineries || tour.days?.length || 0} wineries` },
          ].map((stat, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#5A6E48" }}>
              <span style={{ color: "#6A7E58" }}>{stat.icon}</span>
              {stat.label}
            </div>
          ))}
          {/* solver-a's own opening-hours diagnostic (see model.mod's
              min_time_tw problem) — confirms every stop's arrival/visit
              window was actually checked against that winery's calendar,
              not just travel time between stops. */}
          {tour.time_window?.feasible && (
            <div title="Every stop's arrival time was checked against that winery's opening hours" style={{
              display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 500,
              color: "#3D6B45", background: "rgba(74,112,82,0.12)", padding: "3px 9px", borderRadius: 100,
            }}>
              <Clock size={11} /> Opening hours checked
            </div>
          )}
        </div>
      </div>

      {/* Narrative */}
      {narrative && (
        <div style={{
          background: "rgba(107,39,55,0.06)", border: "1px solid rgba(107,39,55,0.15)",
          borderRadius: 10, padding: "14px 16px", marginBottom: 20,
        }}>
          <p style={{ fontSize: 13, color: "#4A5E38", lineHeight: 1.6, margin: 0 }}>{narrative}</p>
        </div>
      )}

      {/* Budget */}
      {tour.budget_estimate && (
        <div style={{
          background: "#EDF1E4", border: "1px solid #222", borderRadius: 10,
          padding: "14px 16px", marginBottom: 20,
        }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
            Budget estimate
          </p>
          {Object.entries(tour.budget_estimate).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#6A7E58", textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</span>
              <span style={{ fontSize: 12, color: "#2C3820" }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Wine filter coverage */}
      {tour.wine_coverage && <WineCoverageCard coverage={tour.wine_coverage} />}
      {tour.wine_coverage_curve && <WineCoverageCurve curve={tour.wine_coverage_curve} />}
      <AlternateRoutesCard tour={tour} onSwitch={onSwitchAlternative} switching={switchingAlternative} />

      {/* Itinerary — merged day list + leg-by-leg breakdown: every travel
          hop and winery stop, in order, grouped by day. Replaces what used
          to be two separately-derived views. */}
      <p style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
        Itinerary
      </p>
      <DayList
        legs={tour.legs} overnightStops={tour.overnight_stops}
        onSelectWinery={onSelectWinery} onBookWinery={onBookWinery}
        userEmail={userEmail} legStatus={legStatus} departureAt={departureAt}
      />

      {/* Booking tips */}
      {tour.booking_tips && (
        <div style={{ fontSize: 12, color: "#6A7E58", padding: "12px 0", borderTop: "1px solid #1E1E1E", marginTop: 8 }}>
          💡 {tour.booking_tips}
        </div>
      )}
    </div>
  );
}

// ── Chat panel (30%) ──────────────────────────────────────────────────────────

function ChatMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 8 }}>
      {!isUser && (
        <div style={{
          width: 22, height: 22, borderRadius: "50%",
          background: "rgba(107,39,55,0.12)", border: "1px solid rgba(107,39,55,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginRight: 6, marginTop: 2, flexShrink: 0,
        }}>
          <span style={{ color: "#886070", fontSize: 8, fontWeight: 700 }}>W</span>
        </div>
      )}
      <div style={{
        maxWidth: "82%", padding: "8px 11px",
        borderRadius: isUser ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
        background: isUser ? "#6B2737" : "#E2E8D8",
        color: "#2C3820", fontSize: 12, lineHeight: 1.5,
        border: isUser ? "none" : "1px solid #252525",
        whiteSpace: "pre-wrap",
      }}>
        {msg.content}
      </div>
    </div>
  );
}

function ChatPanel({ planId, preferences }) {
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Your tour is ready! Ask me to make any changes — add more wineries, adjust the budget, focus on specific wine styles, or anything else.",
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sessionId] = useState(() => `results-${planId || Date.now()}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.chat(sessionId, msg, history);
      setMessages(prev => [...prev, { role: "assistant", content: res.reply }]);
      setHistory(res.conversation_history || []);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong." }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, history]);

  const handleKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  }, [send]);

  const QUICK = [
    "Add more wineries",
    "Make it more budget-friendly",
    "Focus on organic producers",
    "Book all tastings",
  ];

  return (
    <div style={{
      height: "100%", display: "flex", flexDirection: "column",
      borderLeft: "1px solid #1E1E1E",
    }}>
      {/* Header */}
      <div style={{
        padding: "16px 16px 12px",
        borderBottom: "1px solid #1E1E1E",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          background: "rgba(107,39,55,0.12)", border: "1px solid rgba(107,39,55,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
        }}>🍷</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#2C3820" }}>Refine your tour</div>
          <div style={{ fontSize: 10, color: "#7A8E68" }}>Ask me to make changes</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 12px 6px" }}>
        {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 0" }}>
            <div style={{
              width: 22, height: 22, borderRadius: "50%",
              background: "rgba(107,39,55,0.12)", border: "1px solid rgba(107,39,55,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center", marginRight: 6,
            }}>
              <span style={{ color: "#886070", fontSize: 8, fontWeight: 700 }}>W</span>
            </div>
            {[0,1,2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: "50%", background: "#333",
                animation: `bounce 1.2s ease-in-out ${i*0.2}s infinite`,
              }} />
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick replies */}
      <div style={{ padding: "8px 10px 4px", display: "flex", flexWrap: "wrap", gap: 5 }}>
        {QUICK.map(q => (
          <button key={q} onClick={() => send(q)} style={{
            fontSize: 10, padding: "4px 10px", borderRadius: 100,
            background: "rgba(107,39,55,0.06)", border: "1px solid rgba(107,39,55,0.15)",
            color: "#886070", cursor: "pointer", whiteSpace: "nowrap",
            transition: "background 0.15s",
          }}>{q}</button>
        ))}
      </div>

      {/* Input */}
      <div style={{
        padding: "8px 10px 10px", borderTop: "1px solid #1E1E1E",
        display: "flex", gap: 6, alignItems: "flex-end",
      }}>
        <textarea
          ref={inputRef} value={input}
          onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
          placeholder="Ask to change the tour…" rows={1}
          style={{
            flex: 1, border: "1px solid #222", borderRadius: 8,
            padding: "7px 10px", fontSize: 12,
            fontFamily: "'Inter', sans-serif",
            background: "#E8EDE0", color: "#2C3820",
            resize: "none", lineHeight: 1.4, maxHeight: 64,
            outline: "none", transition: "border-color 0.15s",
          }}
          onFocus={e => e.target.style.borderColor = "rgba(107,39,55,0.35)"}
          onBlur={e => e.target.style.borderColor = "#C8D4B0"}
          onInput={e => { e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,64)+"px"; }}
        />
        <button onClick={() => send()} disabled={!input.trim() || loading} style={{
          width: 32, height: 32, borderRadius: 8,
          background: input.trim() && !loading ? "#6B2737" : "#EAEFE2",
          border: "none", cursor: input.trim() && !loading ? "pointer" : "default",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, transition: "background 0.15s",
        }}>
          <Send size={13} color={input.trim() && !loading ? "rgba(220,228,236,0.9)" : "#333"} />
        </button>
      </div>
    </div>
  );
}

// ── Loading state ─────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={{
      height: "100vh", background: "#F2F5EC",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 16,
    }}>
      <div style={{ fontSize: 32 }}>🍷</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#1E2C14" }}>
        Planning your tour…
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: "50%", background: "#6B2737",
            animation: `bounce 1.2s ease-in-out ${i*0.2}s infinite`,
          }} />
        ))}
      </div>
      <p style={{ fontSize: 12, color: "#7A8E68", maxWidth: 260, textAlign: "center" }}>
        We're selecting the best wineries and crafting your itinerary
      </p>
    </div>
  );
}

// ── Main results page ─────────────────────────────────────────────────────────

export default function TourResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const planId = searchParams.get("plan");
  const { user: traveler } = useAuth("traveler");

  const [tour, setTour] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [selectedWinery, setSelectedWinery] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [wineryWines, setWineryWines] = useState([]);
  const [winesLoading, setWinesLoading] = useState(false);
  const [winesError, setWinesError] = useState("");
  const [bookingWinery, setBookingWinery] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [error, setError] = useState("");
  // "Cannot build a tour under the provided parameters" — a legitimate,
  // correctly-interpreted solver outcome (e.g. every candidate winery
  // closed on the trip's only day), distinct from `error` (a genuine
  // technical failure). See plan/handler.py's status: "infeasible" and
  // orchestrator/handler.py's _store_infeasible (2026-07-27).
  const [infeasibleMessage, setInfeasibleMessage] = useState("");
  const [bookTourOpen, setBookTourOpen] = useState(false);

  // departure_at comes from PlanPage's navigation state (fast path) or a
  // localStorage fallback keyed by plan_id (survives a refresh/direct
  // link — see PlanPage.jsx's handleStart). Falls back to tomorrow 8AM if
  // neither is present, e.g. this results page was opened without going
  // through the Plan page's departure picker.
  const [departureAt] = useState(() => {
    if (location.state?.departure_at) return location.state.departure_at;
    if (planId) {
      try {
        const stored = localStorage.getItem(`wr_departure_${planId}`);
        if (stored) return stored;
      } catch {}
    }
    return defaultDepartureAt();
  });

  // One trip_ref for this (plan_id, departure_at) pair, shared by both
  // BookingModal (ad-hoc "Book tasting") and BookTourStepper ("Book this
  // tour") so a booking made via either counts toward the other's
  // earliest-arrival/dwell-time math. See deriveTripRef above.
  const [tripRef, setTripRef] = useState(null);
  useEffect(() => {
    if (!planId || !departureAt) return;
    let cancelled = false;
    deriveTripRef(planId, departureAt).then(t => { if (!cancelled) setTripRef(t); });
    return () => { cancelled = true; };
  }, [planId, departureAt]);

  // legStatus[legIndex] = { loading, eta, existingBooking, feasible, message }
  // — one entry per winery-stop leg, populated from the SAME trip-aware
  // /booking/availability call both booking flows use (see api.js). Drives
  // the ETA badge and "already booked" badge on WineryStopCard.
  const [legStatus, setLegStatus] = useState({});

  const fetchLegStatus = useCallback((legIndex) => {
    const leg = tour?.legs?.[legIndex];
    if (!leg?.to_winery_id || !planId || !departureAt || !tripRef) return;
    setLegStatus(s => ({ ...s, [legIndex]: { ...(s[legIndex] || {}), loading: true } }));
    api.getBookingAvailability(leg.to_winery_id, {
      planId, legIndex, departureAt, tripRef, partySize: preferences?.party_size || 2,
    }).then(res => {
      setLegStatus(s => ({ ...s, [legIndex]: {
        loading: false, eta: res.earliest_arrival, existingBooking: res.existing_booking,
        feasible: res.feasible, message: res.message,
      }}));
    }).catch(() => {
      setLegStatus(s => ({ ...s, [legIndex]: { loading: false, error: true } }));
    });
  }, [tour, planId, departureAt, tripRef, preferences]);

  // Populate ETA/booking-status for every winery stop once the plan and
  // trip context are ready.
  useEffect(() => {
    if (!tour || !planId || !departureAt || !tripRef) return;
    (tour.legs || []).forEach((leg, i) => {
      if (leg.is_winery_stop && leg.to_winery_id) fetchLegStatus(i);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, planId, departureAt, tripRef]);

  // After a booking is made anywhere (ad-hoc "Book tasting" or "Book this
  // tour"), every downstream stop's dwell-time sum (and this stop's own
  // "already booked" status) may have changed — recalculate this stop and
  // everything after it, per the recalculation rule: booking w[1] recalcs
  // stops after it; booking w[2] recalcs stops after that, etc.
  const handleBooked = useCallback((legIndex) => {
    (tour?.legs || []).forEach((leg, i) => {
      if (i >= legIndex && leg.is_winery_stop && leg.to_winery_id) fetchLegStatus(i);
    });
  }, [tour, fetchLegStatus]);

  // Opens BookingModal directly against a known leg — used by
  // WineryStopCard's "Book tasting" button and the detail modal's, so
  // neither needs to re-resolve which leg a winery belongs to.
  const openBookingForLeg = useCallback((legIndex) => {
    const leg = tour?.legs?.[legIndex];
    if (!leg) return;
    setBookingWinery({
      winery: { winery_id: leg.to_winery_id, name: leg.to, title_image_url: leg.title_image_url },
      legIndex,
    });
  }, [tour]);

  // Opens the winery detail modal from a known leg, normalizing the
  // nested leg.winery object (see summarization/handler.py's
  // build_leg_breakdown) plus leg-level fields into the flat shape the
  // detail modal already expects.
  const openWineryDetailForLeg = useCallback((leg, legIndex = null) => {
    if (!leg) return;
    setSelectedWinery({
      ...(leg.winery || {}),
      winery_id: leg.to_winery_id,
      name: leg.to,
      title_image_url: leg.title_image_url,
      legIndex,
    });
    setDetailTab("overview");
  }, []);
  const openWineryDetail = useCallback(
    (legIndex) => openWineryDetailForLeg(tour?.legs?.[legIndex], legIndex),
    [tour, openWineryDetailForLeg]);

  // Promote a tied alternate route to the main itinerary. The swap is
  // persisted server-side (booking/ETA read the stored plan's legs by
  // index), then the returned tour replaces the current one and per-stop
  // ETA/booking status is re-fetched for the new legs.
  const [switchingAlt, setSwitchingAlt] = useState(false);
  const switchAlternative = useCallback(async (sol) => {
    setSwitchingAlt(true);
    try {
      const res = await api.switchAlternative(planId, (sol.route_names || []).join("|"));
      if (res?.tour) { setLegStatus({}); setTour(res.tour); }
    } catch (e) {
      window.alert(e.message || "Could not switch route.");
    } finally { setSwitchingAlt(false); }
  }, [planId]);

  useEffect(() => {
    if (!selectedWinery?.winery_id) { setWineryWines([]); return; }
    setWinesLoading(true);
    setWinesError("");
    api.getWineryWines(selectedWinery.winery_id)
      .then(setWineryWines)
      .catch(err => setWinesError(err.message))
      .finally(() => setWinesLoading(false));
  }, [selectedWinery?.winery_id]);

  useEffect(() => {
    if (!planId) { navigate("/plan"); return; }
    let cancelled = false;
    const poll = async () => {
      const start = Date.now();
      while (!cancelled && Date.now() - start < 120000) {
        try {
          const res = await api.getPlan(planId);
          if (cancelled) return;
          if (res.status === "ready" && res.tour) {
            setTour(res.tour); setNarrative(res.narrative || ""); setPreferences(res.preferences);
            setLoading(false); return;
          }
          // Status ready but tour null — keep polling briefly
          if (res.status === "infeasible") {
            setInfeasibleMessage(res.infeasible_message || "The wineries on this route are closed for the days and times you selected.");
            setPreferences(res.preferences);
            setLoading(false);
            return;
          }
          if (res.status === "ready" && !res.tour) {
            await new Promise(r => setTimeout(r, 3000));
            continue;
          }
          if (res.status === "error") { setError("Tour generation failed."); setLoading(false); return; }
          await new Promise(r => setTimeout(r, 2000));
        } catch (err) {
          if (!cancelled) { setError(err.message); setLoading(false); }
          return;
        }
      }
      if (!cancelled) { setError("Timed out. Please try again."); setLoading(false); }
    };
    poll();
    return () => { cancelled = true; };
  }, [planId]);

  if (loading) return <LoadingState />;

  if (error) return (
    <div style={{
      height: "100vh", background: "#F2F5EC",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 12,
    }}>
      <p style={{ fontSize: 14, color: "#886070" }}>Failed to load tour: {error}</p>
      <button onClick={() => navigate("/plan")} style={{
        padding: "10px 20px", background: "#6B2737", border: "none",
        borderRadius: 8, color: "#DCE4EC", cursor: "pointer", fontSize: 13,
      }}>Try again</button>
    </div>
  );

  // Legitimate solver result, not an error — every candidate route failed
  // to respect opening hours/time limits under the chosen days/departure
  // time, so there is no tour to show. Distinct copy and action (adjust
  // parameters) rather than the generic "try again" retry above.
  if (infeasibleMessage) return (
    <div style={{
      height: "100vh", background: "#F2F5EC",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 12, padding: 24, textAlign: "center",
    }}>
      <p style={{ fontSize: 16, color: "#6B2737", fontWeight: 600, maxWidth: 420 }}>
        Can't build this tour
      </p>
      <p style={{ fontSize: 14, color: "#886070", maxWidth: 420 }}>{infeasibleMessage}</p>
      <p style={{ fontSize: 13, color: "#886070", maxWidth: 420 }}>
        Try different dates, departure time, or fewer days.
      </p>
      <button onClick={() => navigate("/plan")} style={{
        padding: "10px 20px", background: "#6B2737", border: "none",
        borderRadius: 8, color: "#DCE4EC", cursor: "pointer", fontSize: 13,
      }}>Adjust parameters</button>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-4px)}}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:#252525;border-radius:3px}
      `}</style>

      {/* Top bar */}
      <div style={{
        height: 52, background: "rgba(17,17,17,0.96)", backdropFilter: "blur(10px)",
        borderBottom: "1px solid #1E1E1E",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", position: "sticky", top: 0, zIndex: 40,
      }}>
        <button onClick={() => navigate("/plan")} style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "none", border: "none", color: "#5A6E48", cursor: "pointer", fontSize: 13,
        }}>
          ← Back
        </button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: "#1E2C14" }}>
          {ROAD_NAMES[tour?.road_slug] || "Your Tour"}
        </span>
<button onClick={() => navigate("/plan")} style={{
            display: "flex", alignItems: "center", gap: 5,
            background: "none", border: "1px solid #252525", borderRadius: 7,
            color: "#5A6E48", cursor: "pointer", fontSize: 11, padding: "5px 10px",
          }}>
            <RotateCcw size={11} /> New tour
          </button>
      </div>

      {/* Main layout */}
      <div style={{ position: "relative", height: "calc(100vh - 52px)", background: "#F2F5EC", overflow: "hidden" }}>
        {/* Itinerary — full width */}
        <div style={{ height: "100%", overflowY: "auto" }}>
          <ItineraryPanel tour={tour} narrative={narrative} onShowMap={() => setShowMap(true)} onSelectWinery={openWineryDetail} onSwitchAlternative={switchAlternative} switchingAlternative={switchingAlt} onBookWinery={openBookingForLeg} userEmail={preferences?.user_email} canBookTour={!!(traveler?.email || preferences?.user_email)} onBookTour={() => setBookTourOpen(true)} legStatus={legStatus} departureAt={departureAt} />
        </div>
        {/* Chat toggle tab */}
        <button onClick={() => setChatOpen(o => !o)} style={{
          position: "fixed",
          right: chatOpen ? "min(90vw, 480px)" : 0,
          top: "30%",
          zIndex: 50,
          background: "#8B1A2F", color: "white",
          border: "none", borderRadius: "12px 0 0 12px",
          padding: "14px 10px",
          cursor: "pointer",
          boxShadow: "-2px 0 12px rgba(0,0,0,0.25)",
          transition: "right 0.3s ease",
          fontSize: 13, fontWeight: 700,
          minWidth: 44, minHeight: 44,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {chatOpen ? "✕" : "💬"}
        </button>
        {/* Chat panel — slides in from right */}
        <div style={{
          position: "fixed", top: 52, right: chatOpen ? 0 : "min(-90vw, -480px)",
          width: "min(90vw, 480px)",
          height: "calc(100vh - 52px)",
          background: "#F2F5EC",
          borderLeft: "1px solid #C4D0A8",
          boxShadow: chatOpen ? "-4px 0 20px rgba(0,0,0,0.1)" : "none",
          transition: "right 0.3s ease",
          zIndex: 49,
          overflowY: "hidden",
        }}>
          <ChatPanel planId={planId} preferences={preferences} />
        </div>
      </div>


    {/* Booking modal — ad-hoc "Book tasting", any winery in the plan. When
        bookingWinery.legIndex resolved (see openBooking), this runs the
        same trip-aware feasibility check as "Book this tour"; otherwise it
        falls back to plain month availability with no trip context. */}
    {bookingWinery && (
      <BookingModal
        winery={bookingWinery.winery}
        planId={planId}
        legIndex={bookingWinery.legIndex}
        departureAt={departureAt}
        tripRef={tripRef}
        partySize={preferences?.party_size || 2}
        onBooked={handleBooked}
        onClose={() => setBookingWinery(null)}
      />
    )}

    {/* Book this tour — sequential per-stop stepper */}
    {bookTourOpen && tour && (
      <BookTourStepper
        tour={tour}
        planId={planId}
        departureAt={departureAt}
        tripRef={tripRef}
        userEmail={traveler?.email || preferences?.user_email || ""}
        userName={traveler?.full_name || ""}
        partySize={preferences?.party_size || 2}
        onBooked={handleBooked}
        onClose={() => setBookTourOpen(false)}
      />
    )}

    {/* Map overlay */}
    {showMap && tour && (
      <TourMap tour={tour} onClose={() => setShowMap(false)} onBookWinery={openBookingForLeg} />
    )}

    {/* Winery detail modal */}
    {selectedWinery && (
      <div onClick={() => setSelectedWinery(null)} style={{
        position:"fixed",inset:0,zIndex:900,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(4px)",
        display:"flex",alignItems:"center",justifyContent:"center",padding:20,
      }}>
        <div onClick={e=>e.stopPropagation()} style={{
          background:"#EDF1E4",border:"1px solid #252525",borderRadius:12,padding:24,
          maxWidth:460,width:"100%",maxHeight:"85vh",overflowY:"auto",
        }}>
          {selectedWinery.title_image_url && (
            <div style={{
              width:"100%",height:160,borderRadius:8,marginBottom:16,
              background:`#E8EDE0 url(${selectedWinery.title_image_url}) center/cover no-repeat`,
              border:"1px solid #252525",
            }}/>
          )}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
            <div>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"#1E2C14",margin:"0 0 4px"}}>{selectedWinery.name}</h2>
              {selectedWinery.city && <div style={{fontSize:11,color:"#6A7E58"}}>{selectedWinery.city}{selectedWinery.country ? `, ${selectedWinery.country}` : ""}</div>}
            </div>
            <button onClick={()=>setSelectedWinery(null)} style={{background:"none",border:"none",color:"#5A6E48",cursor:"pointer",fontSize:18,marginLeft:12,flexShrink:0}}>✕</button>
          </div>

          {/* Tabs */}
          <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid #C4D0A8"}}>
            {[
              {id:"overview", label:"Overview"},
              {id:"wines", label:"Wines"},
            ].map(t => (
              <button key={t.id} onClick={()=>setDetailTab(t.id)} style={{
                padding:"7px 4px 9px", marginRight:16, background:"none", border:"none",
                borderBottom: detailTab===t.id ? "2px solid #6B2737" : "2px solid transparent",
                color: detailTab===t.id ? "#6B2737" : "#6A7E58",
                fontSize:12.5, fontWeight: detailTab===t.id ? 600 : 500, cursor:"pointer",
              }}>{t.label}</button>
            ))}
          </div>

          {detailTab === "overview" && (<>
          {selectedWinery.description && <p style={{fontSize:13,color:"#4A5E38",lineHeight:1.6,marginBottom:16}}>{selectedWinery.description}</p>}
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            {selectedWinery.avg_rating && <div style={{fontSize:12,color:"#886070"}}>★ {selectedWinery.avg_rating}</div>}
            {selectedWinery.review_count && <div style={{fontSize:12,color:"#5A6E48"}}>{selectedWinery.review_count} reviews</div>}
            {selectedWinery.visit_duration_hours && <div style={{fontSize:12,color:"#5A6E48"}}>⏱ {selectedWinery.visit_duration_hours}h visit</div>}
            {selectedWinery.price_range && <div style={{fontSize:12,color:"#5A6E48"}}>{selectedWinery.price_range}</div>}
            {selectedWinery.organic && <div style={{fontSize:12,color:"#4A7052"}}>🌿 Organic</div>}
          </div>
          {selectedWinery.wine_styles?.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Wine styles</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {selectedWinery.wine_styles.map((s,i)=>(
                  <span key={i} style={{fontSize:11,padding:"3px 10px",borderRadius:100,background:"rgba(107,39,55,0.08)",color:"#886070",border:"1px solid rgba(107,39,55,0.15)"}}>{s}</span>
                ))}
              </div>
            </div>
          )}
          {selectedWinery.highlights?.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Highlights</div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {selectedWinery.highlights.map((h,i)=>(
                  <div key={i} style={{fontSize:12,color:"#4A5E38",display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:"#6B2737",fontSize:10}}>●</span>{h}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(selectedWinery.opening_hours || selectedWinery.address || selectedWinery.phone || selectedWinery.website) && (
            <div style={{background:"#EDF1E4",border:"1px solid #1E1E1E",borderRadius:8,padding:"12px 14px",marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>Practical info</div>
              {selectedWinery.opening_hours && <div style={{fontSize:12,color:"#5A6E48",marginBottom:4}}>🕐 {selectedWinery.opening_hours}</div>}
              {selectedWinery.address && <div style={{fontSize:12,color:"#5A6E48",marginBottom:4}}>📍 {selectedWinery.address}</div>}
              {selectedWinery.phone && <div style={{fontSize:12,color:"#5A6E48",marginBottom:4}}>📞 {selectedWinery.phone}</div>}
              {selectedWinery.website && (
                <a href={selectedWinery.website} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:12,color:"#886070",textDecoration:"none",display:"block"}}>
                  🌐 {selectedWinery.website}
                </a>
              )}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            {preferences?.user_email ? (
              <button onClick={() => openBookingForLeg(selectedWinery.legIndex)} style={{flex:1,padding:"11px",background:"#6B2737",border:"none",borderRadius:8,color:"#DCE4EC",fontSize:13,fontWeight:500,cursor:"pointer"}}>
                Book tasting
              </button>
            ) : (
              <div style={{flex:1,padding:"11px",borderRadius:8,background:"#EDF1E4",border:"1px solid #C4D0A8",fontSize:13,color:"#6A7E58",textAlign:"center"}}>
                Add email to enable booking
              </div>
            )}
            {selectedWinery.website && (
              <a href={selectedWinery.website} target="_blank" rel="noopener noreferrer" style={{
                flex:1,padding:"11px",background:"#E8EDE0",border:"1px solid #252525",borderRadius:8,
                color:"#5A6E48",fontSize:13,textAlign:"center",textDecoration:"none",
                display:"flex",alignItems:"center",justifyContent:"center",
              }}>Visit website</a>
            )}
          </div>
          </>)}

          {detailTab === "wines" && (
            <div>
              {!selectedWinery.winery_id ? (
                <p style={{fontSize:12,color:"#7A8E68",textAlign:"center",padding:"24px 0"}}>Wine details aren't available for this winery yet.</p>
              ) : winesLoading ? (
                <p style={{fontSize:12,color:"#7A8E68",textAlign:"center",padding:"24px 0"}}>Loading wines…</p>
              ) : winesError ? (
                <p style={{fontSize:12,color:"#886070",textAlign:"center",padding:"24px 0"}}>{winesError}</p>
              ) : wineryWines.length === 0 ? (
                <p style={{fontSize:12,color:"#7A8E68",textAlign:"center",padding:"24px 0"}}>No wines listed for this winery yet.</p>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {wineryWines.map(w => <WineCard key={w.binding_id} w={w} />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    </>
  );
}
